// Editing a draft written in Markdown as if it were formatted text: making words bold, a line a
// heading, the next item of a list on Enter, a table's next cell on Tab, and deleting what reads
// as one character even where the formatting around it is written with several. Each edit takes
// the draft and the selection, as offsets in it, and gives back both, changed.
import { HIGHLIGHTS, TEXT_COLORS, type Highlight, type TextColor } from './colors';
import {
  codeBlocks,
  hiddenRanges,
  lineAt,
  marksAt,
  parseMarkdown,
  rowLine,
  type Line,
  type MarkKind,
} from './markdown';

/** The draft once edited, and what's selected in it: [start, end], the same for a caret. */
export interface Edit {
  readonly text: string;
  readonly selection: readonly [number, number];
}

/** Formatting the toolbar turns on and off within lines. */
export type Format = 'strong' | 'em' | 'underline' | 'strike' | 'sub' | 'sup' | 'code';
export const FORMATS: readonly Format[] = [
  'strong',
  'em',
  'underline',
  'strike',
  'sub',
  'sup',
  'code',
];

/** What the toolbar can make a line. */
export type Block =
  | 'paragraph'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5'
  | 'heading6'
  | 'quote'
  | 'bullet'
  | 'ordered'
  | 'task'
  | 'code';

/** Formatting to give the next words typed, or take from them, as Bold does with no selection. */
export type Pending = Readonly<Partial<Record<Format, boolean>>> & {
  /** A color for them, or null for none. */
  readonly color?: TextColor | null;
  readonly highlight?: Highlight | null;
};

/** What a color can be given to: the words, or what's behind them. */
export type Paint = 'color' | 'highlight';

/** What the table menu does to the table the caret is in. */
export type TableAction = 'addRow' | 'addColumn' | 'deleteRow' | 'deleteColumn' | 'deleteTable';

/** The formatting where the selection is, for the toolbar to show. */
export interface Formats {
  readonly marks: ReadonlySet<Format>;
  /** Where the link the selection is in goes. */
  readonly link: string | null;
  /** What the selected lines are, when they're all the same. */
  readonly block: Block | null;
  /** The color the selected words are in, and what they're highlighted with. */
  readonly color: TextColor | null;
  readonly highlight: Highlight | null;
  /** Whether the selection starts in a table. */
  readonly table: boolean;
}

/** The formatting on a character: its marks, and where its link goes. */
export interface Marks {
  readonly strong?: boolean;
  readonly em?: boolean;
  readonly underline?: boolean;
  readonly strike?: boolean;
  readonly sub?: boolean;
  readonly sup?: boolean;
  readonly code?: boolean;
  readonly href?: string | undefined;
  readonly color?: TextColor | undefined;
  readonly highlight?: Highlight | undefined;
}

/** A character of a line as it reads, with its formatting and where it's written. */
interface Glyph {
  readonly char: string;
  readonly at: number;
  readonly marks: Marks;
  /** Which of a table row's cells it's in. */
  readonly cell?: number;
}

/** Where a line's characters are once it's rewritten, to put the selection back around them. */
interface Places {
  /** Where the line's text starts. */
  readonly from: number;
  /** Where each character is. */
  readonly at: readonly number[];
}

// Part of a word, in writing with spaces between them.
const WORD = /[\p{L}\p{M}\p{N}_'’]/u;
const SPACE = /\s/u;
const LIST: ReadonlySet<Line['kind']> = new Set(['bullet', 'ordered', 'task']);
// Lines taken as written, without formatting of their own: code, and the fences around it.
const RAW: ReadonlySet<Line['kind']> = new Set(['code', 'fence']);
// A table's lines: its rows, and the line under its header.
const TABLE: ReadonlySet<Line['kind']> = new Set(['row', 'delimiter']);
// Writing that would start a list, a heading, or a quote, were it at the start of a line, or that
// would be a divider.
const BLOCK_START =
  /^(?: {0,3}(?:#{1,6}(?:[ \t]|$)|>|`{3}|~{3})|[ \t]*(?:[-*+][ \t]|\d{1,9}[.)][ \t]))|^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/u;
// Outermost first: a link can be bold, and code can be in a link, but not the other way round.
// Tags go outside bold and italics, whose ** and * only work against the letters they format.
const ORDER: readonly MarkKind[] = [
  'link',
  'color',
  'highlight',
  'underline',
  'sub',
  'sup',
  'strong',
  'em',
  'strike',
  'code',
];
// Formatting written with the same characters on either side, or as tags.
const OPENERS: Readonly<Record<Exclude<MarkKind, 'link' | Paint>, string>> = {
  strong: '**',
  em: '*',
  underline: '<u>',
  strike: '~~',
  sub: '<sub>',
  sup: '<sup>',
  code: '`',
};
// What starts or ends a tag the draft reads as formatting, written as it would be.
const TAG_START = /^\/?(?:u|sub|sup|mark|span)[\s>]/iu;

const ordered = (a: number, b: number): [number, number] => (a <= b ? [a, b] : [b, a]);
// A divider as Jira makes one when it's typed.
const DASHES = /^ {0,3}-{3,}[ \t]*$/u;
const indentOf = (text: string, line: Line) =>
  /^[ \t]*/u.exec(text.slice(line.start, line.from))?.[0] ?? '';

function blockOf(line: Line): Block {
  switch (line.kind) {
    case 'heading':
      return `heading${String(Math.min(Math.max(line.level, 1), 6))}` as Block;
    case 'fence':
    case 'code':
      return 'code';
    case 'rule':
    case 'row':
    case 'delimiter':
      return 'paragraph';
    default:
      return line.kind;
  }
}

/** The lines a selection touches, or the caret's line. */
function selectedLines(all: readonly Line[], start: number, end: number): Line[] {
  return all.filter((line) => line.start <= end && line.end >= start);
}

/** A line's characters as they read, from the start of its text to its end. */
function glyphsOf(text: string, line: Line): Glyph[] {
  const hidden = hiddenRanges(line);
  const glyphs: Glyph[] = [];
  let range = 0;
  for (let at = line.from; at < line.end; at += 1) {
    while ((hidden[range]?.[1] ?? Infinity) <= at) range += 1;
    if ((hidden[range]?.[0] ?? Infinity) <= at) continue;
    const marks: { -readonly [K in keyof Marks]: Marks[K] } = {};
    for (const span of marksAt(line, at)) {
      if (span.kind === 'link') marks.href = span.href;
      else if (span.kind === 'color') marks.color = span.color as TextColor;
      else if (span.kind === 'highlight') marks.highlight = span.color as Highlight;
      else marks[span.kind] = true;
    }
    const cell = line.kind === 'row' ? cellIndex(line, at) : -1;
    glyphs.push({ char: text[at] ?? '', at, marks, ...(cell >= 0 && { cell }) });
  }
  return glyphs;
}

/** The cell of a table row that `offset` is in, or at the edge of: the one before, between two. */
function cellIndex(line: Line, offset: number): number {
  let index = 0;
  line.cells.forEach((cell, at) => {
    if (cell.from <= offset) index = at;
  });
  return index;
}

/** A place in a row's cells for `offset`: the nearest edge of a cell, where it's between them. */
function inCell(line: Line, offset: number): number {
  const cell = line.cells[cellIndex(line, offset)];
  if (!cell) return offset;
  return Math.min(Math.max(offset, cell.from), cell.to);
}

/** How many of the characters, as they read, come before `offset`. */
const countBefore = (glyphs: readonly Glyph[], offset: number) =>
  glyphs.filter((glyph) => glyph.at < offset).length;

const sameMarks = (a: Marks, b: Marks) =>
  FORMATS.every((kind) => Boolean(a[kind]) === Boolean(b[kind])) &&
  a.href === b.href &&
  a.color === b.color &&
  a.highlight === b.highlight;

/** What a mark is on a character: where its link goes, its color, or just that it's there. */
const valueOf = (marks: Marks, kind: MarkKind): string | boolean | undefined =>
  kind === 'link' ? marks.href : marks[kind];

const has = (marks: Marks, kind: MarkKind) => Boolean(valueOf(marks, kind));

function withMark(marks: Marks, kind: Format, on: boolean): Marks {
  // Words are raised or lowered, not both.
  if (on && kind === 'sub') return { ...marks, sub: true, sup: false };
  if (on && kind === 'sup') return { ...marks, sup: true, sub: false };
  return { ...marks, [kind]: on };
}

/**
 * Characters and their formatting, written as Markdown. Spaces at the edges of bold, italics,
 * and strikethrough go just outside them, where Markdown needs them to be, and those of
 * underline and the rest with them.
 */
function serialize(glyphs: readonly Glyph[], cell = false): string {
  const marks: Marks[] = glyphs.map((glyph) => glyph.marks);
  const trimmed = [
    'strong',
    'em',
    'underline',
    'strike',
    'sub',
    'sup',
    'color',
    'highlight',
  ] as const;
  for (const kind of trimmed) {
    let start = 0;
    while (start < glyphs.length) {
      if (!marks[start]?.[kind]) {
        start += 1;
        continue;
      }
      let end = start;
      while (end < glyphs.length && marks[end]?.[kind]) end += 1;
      const off = (at: number) =>
        kind === 'color' || kind === 'highlight'
          ? { ...marks[at], [kind]: undefined }
          : withMark(marks[at] ?? {}, kind, false);
      for (let at = start; at < end && SPACE.test(glyphs[at]?.char ?? ''); at += 1)
        marks[at] = off(at);
      for (let at = end - 1; at >= start && SPACE.test(glyphs[at]?.char ?? ''); at -= 1)
        marks[at] = off(at);
      start = end;
    }
  }

  let out = '';
  let open: { readonly kind: MarkKind; readonly value: string | boolean | undefined }[] = [];
  const closeTo = (keep: number) => {
    for (let index = open.length - 1; index >= keep; index -= 1) {
      const mark = open[index];
      if (mark) out += closer(mark.kind, mark.value);
    }
    open = open.slice(0, keep);
  };
  let at = 0;
  while (at < glyphs.length) {
    const current = marks[at] ?? {};
    let end = at + 1;
    while (end < glyphs.length && sameMarks(marks[end] ?? {}, current)) end += 1;
    const wanted = ORDER.filter((kind) => kind !== 'code' && has(current, kind));
    let keep = 0;
    while (
      keep < open.length &&
      open[keep]?.kind === wanted[keep] &&
      open[keep]?.value === valueOf(current, open[keep]?.kind ?? 'link')
    )
      keep += 1;
    closeTo(keep);
    for (const kind of wanted.slice(keep)) {
      out += opener(kind, current);
      open.push({ kind, value: valueOf(current, kind) });
    }
    const chars = glyphs.slice(at, end).map((glyph) => glyph.char);
    if (current.code) {
      // Code is innermost, with as many backticks around it as it needs; in a table, its pipes
      // escaped, as Markdown takes them there.
      const code = chars.join('');
      const longest = Math.max(0, ...[...code.matchAll(/`+/gu)].map((match) => match[0].length));
      const ticks = '`'.repeat(longest + 1);
      const pad = /^`|`$/u.test(code) ? ' ' : '';
      out += `${ticks}${pad}${cell ? code.replace(/\|/gu, '\\|') : code}${pad}${ticks}`;
    } else {
      out += escape(chars, glyphs, at, cell);
    }
    at = end;
  }
  closeTo(0);
  return out;
}

/** What starts a mark on a character, as Markdown or a tag: a color's is the color it is. */
function opener(kind: MarkKind, marks: Marks): string {
  switch (kind) {
    case 'link':
      return '[';
    case 'color':
      return `<span style="color: ${TEXT_COLORS[marks.color ?? 'gray']}">`;
    case 'highlight':
      // <mark> alone is yellow, as everywhere that reads it.
      return !marks.highlight || marks.highlight === 'yellow'
        ? '<mark>'
        : `<mark style="background-color: ${HIGHLIGHTS[marks.highlight]}">`;
    default:
      return OPENERS[kind];
  }
}

function closer(kind: MarkKind, value: string | boolean | undefined): string {
  if (kind === 'link') return `](${typeof value === 'string' ? value : ''})`;
  if (kind === 'color') return '</span>';
  if (kind === 'highlight') return '</mark>';
  const written = OPENERS[kind];
  return written.startsWith('<') ? `</${written.slice(1)}` : written;
}

/**
 * Text as Markdown takes it: a backslash before the characters that would otherwise start or end
 * formatting where they are, and only those, so plain writing stays as it was written.
 */
function escape(
  chars: readonly string[],
  glyphs: readonly Glyph[],
  from: number,
  cell: boolean,
): string {
  let out = '';
  chars.forEach((char, index) => {
    const at = from + index;
    const before = glyphs[at - 1]?.char ?? ' ';
    const after = glyphs[at + 1]?.char ?? ' ';
    const touching = !SPACE.test(before) || !SPACE.test(after);
    const risky =
      ((char === '*' || char === '`') && touching) ||
      (char === '_' && touching && !(WORD.test(before) && WORD.test(after))) ||
      (char === '~' && (before === '~' || after === '~')) ||
      (char === '[' &&
        glyphs.some(
          (glyph, later) => later > at && glyph.char === ']' && glyphs[later + 1]?.char === '(',
        )) ||
      (char === '<' &&
        TAG_START.test(
          glyphs
            .slice(at + 1, at + 7)
            .map((glyph) => glyph.char)
            .join(''),
        )) ||
      // A pipe in a table's cell would end it.
      (char === '|' && cell) ||
      (char === '\\' && /[!-/:-@[-`{-~]/u.test(after));
    out += risky ? `\\${char}` : char;
  });
  return out;
}

/**
 * A line written with its marker and its characters' formatting. Where a line without a marker
 * would start a list or a heading, its writing stays writing.
 */
function writeLine(marker: string, glyphs: readonly Glyph[]): string {
  let body = serialize(glyphs);
  if (!marker && BLOCK_START.test(body)) {
    body = /^\s*\d/u.test(body) ? body.replace(/^(\s*\d+)/u, '$1\\') : `\\${body}`;
  }
  return marker + body;
}

/** A table's row, its cells in order, each with its own formatting. */
function writeRow(cells: readonly (readonly Glyph[])[]): string {
  return `| ${cells.map((glyphs) => serialize(glyphs, true)).join(' | ')} |`;
}

/** A line written anew with its characters' formatting, its marker, or a row its cells, kept. */
function rewriteLine(text: string, line: Line, glyphs: readonly Glyph[]): string {
  if (line.kind !== 'row') return writeLine(text.slice(line.start, line.from), glyphs);
  return writeRow(line.cells.map((_, index) => glyphs.filter((glyph) => glyph.cell === index)));
}

/** A line's marker, such as "- ", and its characters with their formatting, as for a paste. */
export interface FormattedLine {
  readonly marker: string;
  readonly chars: readonly { readonly char: string; readonly marks: Marks }[];
  /** A line of code, written as it is. */
  readonly raw?: boolean;
}

/** A table's rows, the first its header, each a list of its cells' characters. */
export type FormattedTable = readonly (readonly (readonly {
  readonly char: string;
  readonly marks: Marks;
}[])[])[];

/** Lines and their formatting, written as Markdown; a table, as a table. */
export function writeLines(lines: readonly (FormattedLine | FormattedTable)[]): string {
  return lines
    .map((line) => {
      if (Array.isArray(line)) return writeTable(line);
      const { marker, chars, raw } = line as FormattedLine;
      return raw
        ? marker + chars.map((one) => one.char).join('')
        : writeLine(
            marker,
            chars.map((one, at) => ({ ...one, at })),
          );
    })
    .join('\n');
}

/** A table, its header first, written as Markdown. */
const writeTable = (rows: FormattedTable) =>
  writeGrid(
    rows.map((row) =>
      row.map((cell) =>
        serialize(
          cell.map((one, at) => ({ ...one, at })),
          true,
        ),
      ),
    ),
  );

/**
 * A table from its cells as they're written, its header first, with as many columns as its widest
 * row, and the line under its header that says it's a table.
 */
function writeGrid(rows: readonly (readonly string[])[]): string {
  const columns = Math.max(1, ...rows.map((row) => row.length));
  const write = (cells: readonly string[]) =>
    `| ${Array.from({ length: columns }, (_, index) => cells[index] ?? '').join(' | ')} |`;
  const delimiter = `| ${Array<string>(columns).fill('---').join(' | ')} |`;
  return [write(rows[0] ?? []), delimiter, ...rows.slice(1).map(write)].join('\n');
}

/** The tables in the draft, each its lines from its header to its last row. */
function tablesIn(all: readonly Line[]): Line[][] {
  const tables: Line[][] = [];
  let current: Line[] | null = null;
  for (const line of all) {
    if (!TABLE.has(line.kind)) {
      current = null;
      continue;
    }
    if (line.header || !current) {
      current = [];
      tables.push(current);
    }
    current.push(line);
  }
  return tables;
}

/** The table `offset` is in, as its lines, or null. */
const tableAt = (all: readonly Line[], offset: number): Line[] | null =>
  tablesIn(all).find(
    (lines) => (lines[0]?.start ?? 0) <= offset && offset <= (lines.at(-1)?.end ?? 0),
  ) ?? null;

/** A table's rows, its header first, without the line under it. */
const rowsOf = (table: readonly Line[]) => table.filter((line) => line.kind === 'row');

/**
 * Where the caret goes in the table at `top`: in its `row`th row, the header first, after the
 * `count`th character of its `column`th cell, or at the end of the cell's text without a count.
 */
function placeInTable(text: string, top: number, row: number, column: number, count?: number) {
  const line = rowsOf(tableAt(parseMarkdown(text), top) ?? [])[row];
  const cell = line?.cells[Math.min(column, line.cells.length - 1)];
  if (!line || !cell) return top;
  if (count === undefined) return cell.to;
  if (count === 0) return cell.from;
  const glyphs = glyphsOf(text, line).filter((glyph) => glyph.cell === line.cells.indexOf(cell));
  const glyph = glyphs[Math.min(count, glyphs.length) - 1];
  return glyph ? glyph.at + 1 : cell.from;
}

/**
 * Takes what's between `start` and `end` from the cells of a table's rows, leaving the cells
 * themselves, and puts the caret where `start` was.
 */
function clearCells(text: string, table: readonly Line[], [start, end]: [number, number]): Edit {
  const rows = rowsOf(table);
  const top = table[0]?.start ?? start;
  const first = rows.findIndex((row) => row.end >= start);
  const firstRow = rows[first];
  if (!firstRow) return { text, selection: [start, start] };
  const column = cellIndex(firstRow, start);
  const count = glyphsOf(text, firstRow).filter(
    (glyph) => glyph.cell === column && glyph.at < start,
  ).length;
  let next = text;
  // From the last row up, so the rows above keep their offsets.
  for (const row of [...rows].reverse()) {
    if (row.end < start || row.start > end) continue;
    const glyphs = glyphsOf(text, row);
    const kept = glyphs.filter((glyph) => glyph.at < start || glyph.at >= end);
    if (kept.length < glyphs.length)
      next = replaceLine(next, row, rewriteLine(next, row, kept)).text;
  }
  const caret = placeInTable(next, top, first, column, count);
  return { text: next, selection: [caret, caret] };
}

/** Puts `written` in place of the line that starts at `start`, and finds its characters. */
function replaceLine(text: string, line: Line, written: string): { text: string; places: Places } {
  const next = text.slice(0, line.start) + written + text.slice(line.end);
  // A row on its own wouldn't read as one, without the rest of its table.
  const [parsed] =
    line.kind === 'row'
      ? [rowLine(written, 0, written.length, line.header, line.columns)]
      : parseMarkdown(written);
  if (!parsed) return { text: next, places: { from: line.start, at: [] } };
  return {
    text: next,
    places: {
      from: line.start + parsed.from,
      at: glyphsOf(written, parsed).map((glyph) => line.start + glyph.at),
    },
  };
}

/** Just after the `count`th character: in its formatting, as the next one typed would be. */
const after = (places: Places, count: number) =>
  count === 0 ? places.from : (places.at[count - 1] ?? places.from - 1) + 1;
/** Just before the `count`th character, in its formatting. */
const before = (places: Places, count: number) => places.at[count] ?? after(places, count);

/** The word the caret is within, as [start, end]; null at a word's edge, or between words. */
export function wordAt(text: string, offset: number): [number, number] | null {
  if (!WORD.test(text[offset - 1] ?? '') || !WORD.test(text[offset] ?? '')) return null;
  let start = offset;
  let end = offset;
  while (start > 0 && WORD.test(text[start - 1] ?? '')) start -= 1;
  while (end < text.length && WORD.test(text[end] ?? '')) end += 1;
  return [start, end];
}

/**
 * Applies `change` to the characters each selected line has between `start` and `end`, line by
 * line, and puts the selection back around the same characters.
 */
function reformat(
  text: string,
  [start, end]: [number, number],
  change: (glyph: Glyph) => Glyph,
  caret: number | null = null,
): Edit {
  const all = parseMarkdown(text);
  const touched = selectedLines(all, start, end).filter((line) => !RAW.has(line.kind));
  let next = text;
  const placed = new Map<Line, Places>();
  // From the last line up, so the lines above keep their offsets.
  for (const line of [...touched].reverse()) {
    const glyphs = glyphsOf(text, line);
    const changed = glyphs.map((glyph) =>
      glyph.at >= start && glyph.at < end ? change(glyph) : glyph,
    );
    const replaced = replaceLine(next, line, rewriteLine(next, line, changed));
    next = replaced.text;
    placed.set(line, replaced.places);
  }
  // Each line's places moved with the lines above it, once those were rewritten.
  const moved = parseMarkdown(next);
  const find = (line: Line) => {
    const now = moved[all.indexOf(line)];
    const places = placed.get(line);
    if (!now || !places) return null;
    const shift = now.start - line.start;
    return { from: places.from + shift, at: places.at.map((at) => at + shift) };
  };
  const first = touched[0];
  const last = touched.at(-1);
  const top = first && find(first);
  const bottom = last && find(last);
  if (!first || !last || !top || !bottom) return { text: next, selection: [start, end] };
  if (caret !== null) {
    const at = after(top, countBefore(glyphsOf(text, first), caret));
    return { text: next, selection: [at, at] };
  }
  const from = countBefore(glyphsOf(text, first), start);
  const to = countBefore(glyphsOf(text, last), end);
  const selStart = first === last && from === to ? after(top, from) : before(top, from);
  return { text: next, selection: [selStart, Math.max(selStart, after(bottom, to))] };
}

/**
 * Gives the selected words a format, or takes it from them if they all have it. A caret within
 * a word formats that word; anywhere else there's nothing to format, and it's null: the next
 * words typed can take the format instead (see `insertText`).
 */
export function toggleMark(
  text: string,
  selection: readonly [number, number],
  kind: Format,
): Edit | null {
  let [start, end] = ordered(selection[0], selection[1]);
  const caret = start === end ? start : null;
  if (caret !== null) {
    const word = wordAt(text, caret);
    if (!word) return null;
    [start, end] = word;
  }
  const all = parseMarkdown(text);
  const words = selectedLines(all, start, end)
    .filter((line) => !RAW.has(line.kind))
    .flatMap((line) => glyphsOf(text, line))
    .filter((glyph) => glyph.at >= start && glyph.at < end && !SPACE.test(glyph.char));
  if (words.length === 0) return null;
  const on = !words.every((glyph) => glyph.marks[kind]);
  return reformat(
    text,
    [start, end],
    (glyph) => ({ ...glyph, marks: withMark(glyph.marks, kind, on) }),
    caret,
  );
}

/**
 * Takes out the draft from `start` to `end`. Formatting whose words all go goes with them, and
 * formatting that keeps some of its words is written around what's left, so nothing that formats
 * the text is ever left showing.
 */
export function removeText(text: string, selection: readonly [number, number]): Edit {
  const [start, end] = ordered(selection[0], selection[1]);
  if (start === end) return { text, selection: [start, start] };
  const all = parseMarkdown(text);
  const first = lineAt(all, start);
  const last = lineAt(all, end);
  // A divider reads as nothing, so it goes only with a selection right across it: one that starts
  // or ends on it leaves it, and its line break, where they are.
  if (first !== last && first.kind === 'rule' && start > first.start)
    return removeText(text, [Math.min(first.end + 1, end), end]);
  if (first !== last && last.kind === 'rule' && end <= last.start && start > first.start)
    return removeText(text, [start, Math.max(start, last.start - 1)]);
  // A table stays one unless it's all selected: what's selected in it goes from its cells, and
  // what's selected around it goes as it would, the line breaks around it kept.
  const table = tablesIn(all)
    .filter((lines) => (lines[0]?.start ?? 0) <= end && (lines.at(-1)?.end ?? 0) >= start)
    .findLast((lines) => !(start <= (lines[0]?.start ?? 0) && end >= (lines.at(-1)?.end ?? 0)));
  if (table) {
    const top = table[0]?.start ?? start;
    const bottom = table.at(-1)?.end ?? end;
    let next = end > bottom + 1 ? removeText(text, [bottom + 1, end]).text : text;
    const cleared = clearCells(next, tableAt(parseMarkdown(next), top) ?? table, [
      Math.max(start, top),
      Math.min(end, bottom),
    ]);
    next = cleared.text;
    if (start >= top) return cleared;
    if (start < top - 1) next = removeText(next, [start, top - 1]).text;
    return { text: next, selection: [start, start] };
  }
  // A code block's fences aren't shown, so they stay unless it's all selected: what's selected of
  // its code goes, and what's selected around it goes as it would, the line breaks around it kept.
  const block = codeBlocks(all)
    .filter((lines) => (lines[0]?.start ?? 0) <= end && (lines.at(-1)?.end ?? 0) >= start)
    .findLast((lines) => !(start <= (lines[0]?.start ?? 0) && end >= (lines.at(-1)?.end ?? 0)));
  if (block) {
    const top = block[0]?.start ?? start;
    const bottom = block.at(-1)?.end ?? end;
    const from = Math.max(start, (block[0]?.end ?? top) + 1);
    const to = Math.min(end, (block.at(-1)?.start ?? bottom) - 1);
    let next = end > bottom + 1 ? removeText(text, [bottom + 1, end]).text : text;
    if (from < to) next = next.slice(0, from) + next.slice(to);
    if (start >= top) return { text: next, selection: [from, from] };
    if (start < top - 1) next = removeText(next, [start, top - 1]).text;
    return { text: next, selection: [start, start] };
  }
  const splice = (from: number, to: number): Edit => ({
    text: text.slice(0, from) + text.slice(to),
    selection: [from, from],
  });

  // Whole lines, from the start of one to the text of another, go with their line breaks.
  if (first !== last && start <= first.start && end <= last.from)
    return splice(first.start, last.start);
  const touched = all.filter((line) => line.start <= end && line.end >= start);
  const plain =
    touched.every((line) => hiddenRanges(line).every(([from, to]) => to <= start || from >= end)) &&
    !touched.some((line) => line.spans.some((span) => span.from >= start && span.to <= end));
  if (plain || RAW.has(first.kind) || RAW.has(last.kind)) return splice(start, end);

  const head = start > first.start ? glyphsOf(text, first).filter((glyph) => glyph.at < start) : [];
  const tail = glyphsOf(text, last).filter((glyph) => glyph.at >= end);
  const base: Line =
    start > first.start ? first : { ...first, kind: 'paragraph', from: first.start };
  const joined: Line = { ...base, end: last.end };
  const { text: next, places } = replaceLine(
    text,
    joined,
    rewriteLine(text, base, [...head, ...tail]),
  );
  const caret = after(places, head.length);
  return { text: next, selection: [caret, caret] };
}

/**
 * Types `input` in place of the selection. With formatting pending, as after Bold with nothing
 * selected, the words typed take it, or leave it.
 */
export function insertText(
  text: string,
  selection: readonly [number, number],
  input: string,
  pending: Pending = {},
): Edit {
  const removed = removeText(text, selection);
  let at = removed.selection[0];
  // Typed on a divider made with ---, as in Jira, the writing carries on in a line below it.
  // Others might be on their way to being something else, such as ***bold***, so they're left to.
  const here = lineAt(parseMarkdown(removed.text), at);
  if (here.kind === 'rule' && DASHES.test(removed.text.slice(here.start, here.end))) {
    const below = `${removed.text.slice(0, here.end)}\n${input}${removed.text.slice(here.end)}`;
    const end = here.end + 1 + input.length;
    return { text: below, selection: [end, end] };
  }
  // In a table, what's typed stays in its cell: on one line, with its pipes escaped.
  if (here.kind === 'row') {
    at = inCell(here, at);
    input = input.replace(/\r?\n/gu, ' ').replace(/\|/gu, '\\|');
  }
  const next = removed.text.slice(0, at) + input + removed.text.slice(at);
  const caret = at + input.length;
  const formats = FORMATS.filter((kind) => pending[kind] !== undefined);
  const paints = (['color', 'highlight'] as const).filter((kind) => pending[kind] !== undefined);
  if ((formats.length === 0 && paints.length === 0) || !input.trim() || input.includes('\n'))
    return { text: next, selection: [caret, caret] };
  const line = lineAt(parseMarkdown(next), at);
  if (RAW.has(line.kind)) return { text: next, selection: [caret, caret] };
  const glyphs = glyphsOf(next, line);
  const typed = countBefore(glyphs, caret);
  const changed = glyphs.map((glyph) => {
    if (glyph.at < at || glyph.at >= caret) return glyph;
    let marks = glyph.marks;
    for (const kind of formats) marks = withMark(marks, kind, pending[kind] === true);
    for (const kind of paints) marks = { ...marks, [kind]: pending[kind] ?? undefined };
    return { ...glyph, marks };
  });
  const replaced = replaceLine(next, line, rewriteLine(next, line, changed));
  const end = after(replaced.places, typed);
  return { text: replaced.text, selection: [end, end] };
}

const graphemes = () => new Intl.Segmenter(undefined, { granularity: 'grapheme' });

/** Where the character, as it reads, before `offset` starts: a whole emoji or accented letter. */
function graphemeBefore(text: string, offset: number, floor: number): number {
  const from = Math.max(floor, offset - 16);
  let start = from;
  for (const { index } of graphemes().segment(text.slice(from, offset))) start = from + index;
  return start;
}

function graphemeAfter(text: string, offset: number, ceiling: number): number {
  const [first] = graphemes().segment(text.slice(offset, Math.min(ceiling, offset + 16)));
  return offset + (first?.segment.length ?? 1);
}

/** Whether the character at `offset` only says how the rest reads. */
const isHidden = (line: Line, offset: number) =>
  hiddenRanges(line).some(([start, end]) => start <= offset && offset < end);

/**
 * Backspace with nothing selected. At the start of a list item, heading, or quote's text, it
 * stops being one; at the start of a line, the line joins the one above; otherwise the character
 * before the caret goes, whatever formatting is written between the two.
 */
export function deleteBackward(text: string, caret: number): Edit {
  const all = parseMarkdown(text);
  const line = lineAt(all, caret);
  if (TABLE.has(line.kind)) return deleteInTable(text, all, line, caret, true);
  let at = caret;
  while (at > line.from && isHidden(line, at - 1)) at -= 1;
  if (at <= line.from && line.from > line.start && !RAW.has(line.kind)) {
    if (LIST.has(line.kind) && line.depth > 0) {
      const outdented = indent(text, [caret, caret], true);
      if (outdented) return outdented;
    }
    return {
      text: text.slice(0, line.start) + text.slice(line.from),
      selection: [line.start, line.start],
    };
  }
  if (at <= line.start) {
    if (line.start === 0) return { text, selection: [caret, caret] };
    // A divider above goes, rather than the line joining it; an empty line goes on its own.
    const above = all[all.indexOf(line) - 1];
    if (above?.kind === 'rule') {
      if (line.end > line.start) return removeLine(text, above);
      const from = line.start - 1;
      return { text: text.slice(0, from) + text.slice(line.start), selection: [from, from] };
    }
    // At the start of a code block, it stops being one, as a quote does.
    if (line.kind === 'code' && above?.kind === 'fence' && above.opens)
      return codeBlock(text, all, [line], [caret, caret]);
    // A table or a code block above stays one: an empty line goes, and otherwise the caret goes to
    // its last cell, or the end of its code.
    const code = above?.kind === 'fence' && !above.opens ? all[all.indexOf(above) - 1] : undefined;
    if (code?.kind === 'code') {
      const next =
        line.end === line.start ? text.slice(0, line.start - 1) + text.slice(line.start) : text;
      return { text: next, selection: [code.end, code.end] };
    }
    if (above && TABLE.has(above.kind)) {
      const row = rowsOf(tableAt(all, above.start) ?? []).at(-1);
      const end = row?.cells.at(-1)?.to ?? above.end;
      const next =
        line.end === line.start ? text.slice(0, line.start - 1) + text.slice(line.start) : text;
      return { text: next, selection: [end, end] };
    }
    return removeText(text, [line.start - 1, line.start]);
  }
  return removeText(text, [
    graphemeBefore(text, at, RAW.has(line.kind) ? line.start : line.from),
    at,
  ]);
}

/**
 * Backspace or Delete in a table: a character of the cell the caret is in, but never the pipes
 * between cells. Backspace at a cell's start goes back a cell instead, or takes an empty row away.
 */
function deleteInTable(
  text: string,
  all: readonly Line[],
  line: Line,
  caret: number,
  backward: boolean,
): Edit {
  const stay: Edit = { text, selection: [caret, caret] };
  const index = cellIndex(line, caret);
  const cell = line.cells[index];
  if (line.kind !== 'row' || !cell) return stay;
  let at = inCell(line, caret);
  if (!backward) {
    while (at < cell.to && isHidden(line, at)) at += 1;
    return at < cell.to ? removeText(text, [at, graphemeAfter(text, at, cell.to)]) : stay;
  }
  while (at > cell.from && isHidden(line, at - 1)) at -= 1;
  if (at > cell.from) return removeText(text, [graphemeBefore(text, at, cell.from), at]);
  const position = all.indexOf(line);
  const above = all[position - 1]?.kind === 'delimiter' ? all[position - 2] : all[position - 1];
  const previous = above?.kind === 'row' ? above : null;
  const back = index > 0 ? line.cells[index - 1]?.to : previous?.cells.at(-1)?.to;
  if (index === 0 && !line.header && previous && line.cells.every((one) => one.from === one.to))
    return { text: removeLine(text, line).text, selection: [back ?? caret, back ?? caret] };
  return back === undefined ? stay : { text, selection: [back, back] };
}

/** Takes out a whole line, with a line break, and puts the caret where it was. */
function removeLine(text: string, line: Line): Edit {
  if (line.end < text.length)
    return {
      text: text.slice(0, line.start) + text.slice(line.end + 1),
      selection: [line.start, line.start],
    };
  const from = Math.max(0, line.start - 1);
  return { text: text.slice(0, from), selection: [from, from] };
}

/** Delete with nothing selected: the character after the caret, or at a line's end, the break. */
export function deleteForward(text: string, caret: number): Edit {
  const all = parseMarkdown(text);
  const line = lineAt(all, caret);
  if (line.kind === 'rule') return removeLine(text, line);
  if (TABLE.has(line.kind)) return deleteInTable(text, all, line, caret, false);
  let at = Math.max(caret, line.from);
  while (at < line.end && isHidden(line, at)) at += 1;
  if (at >= line.end) {
    const next = all[all.indexOf(line) + 1];
    if (!next) return { text, selection: [caret, caret] };
    // Code keeps its fence below it: joined to it, the fence would be code, and the block unclosed.
    if (line.kind === 'code' && next.kind === 'fence') return { text, selection: [caret, caret] };
    // A table or a code block below stays one: an empty line before it goes, and otherwise
    // nothing does.
    if (TABLE.has(next.kind) || codeBlocks(all).some((block) => block[0] === next)) {
      if (line.end > line.start) return { text, selection: [caret, caret] };
      const removed = removeLine(text, line);
      const place = caretPlace(removed.text, removed.selection[0]);
      return { text: removed.text, selection: [place, place] };
    }
    return removeText(text, [line.end, next.from]);
  }
  return removeText(text, [at, graphemeAfter(text, at, line.end)]);
}

/** What starts the next item after `line`: the same kind of marker, with the next number. */
function continuation(text: string, line: Line): string {
  const marker = text.slice(line.start, line.from);
  const indent = indentOf(text, line);
  const bullet = marker.charAt(indent.length);
  switch (line.kind) {
    case 'task':
      return `${indent}${bullet} [ ] `;
    case 'ordered':
      return `${indent}${String(Number.parseInt(line.number, 10) + 1)}${line.number.slice(-1)} `;
    case 'bullet':
      return `${indent}${bullet} `;
    default:
      return marker;
  }
}

/** Numbers the items after the `index`th line in its list on from its number, one each. */
function renumber(text: string, index: number): string {
  const all = parseMarkdown(text);
  const first = all[index];
  if (first?.kind !== 'ordered') return text;
  let number = Number.parseInt(first.number, 10);
  let next = text;
  let shift = 0;
  for (const line of all.slice(index + 1)) {
    if (line.kind === 'ordered' && line.depth === first.depth) {
      number += 1;
      const written = `${String(number)}${line.number.slice(-1)}`;
      const at = line.start + indentOf(text, line).length + shift;
      next = next.slice(0, at) + written + next.slice(at + line.number.length);
      shift += written.length - line.number.length;
    } else if (!(LIST.has(line.kind) && line.depth > first.depth)) break;
  }
  return next;
}

/**
 * A line broken in two at `at`, the second starting with `marker`. Formatting the break falls in,
 * such as bold, is closed before it and opened again after it, so both halves read as they did.
 * The caret goes to the start of the second, in its formatting.
 */
function splitLine(text: string, line: Line, at: number, marker: string): Edit {
  const glyphs = glyphsOf(text, line);
  const first = rewriteLine(
    text,
    line,
    glyphs.filter((glyph) => glyph.at < at),
  );
  const second = writeLine(
    marker,
    glyphs.filter((glyph) => glyph.at >= at),
  );
  const top = line.start + first.length + 1;
  const [parsed] = parseMarkdown(second);
  const start = parsed ? (glyphsOf(second, parsed)[0]?.at ?? parsed.from) : marker.length;
  return {
    text: `${text.slice(0, line.start)}${first}\n${second}${text.slice(line.end)}`,
    selection: [top + start, top + start],
  };
}

/**
 * Enter. In a list or a quote, the next line carries it on; on an empty item, Enter leaves the
 * list instead. Formatting the break falls in carries on in the next line too. ``` then Enter
 * starts a code block, closed below, and Enter on its last two lines, empty, leaves it. Shift+Enter
 * is just a new line.
 */
export function breakLine(text: string, selection: readonly [number, number], soft = false): Edit {
  const removed = removeText(text, selection);
  const draft = removed.text;
  const at = removed.selection[0];
  const all = parseMarkdown(draft);
  const line = lineAt(all, at);
  const splice = (insert: string, caret = at + insert.length): Edit => ({
    text: draft.slice(0, at) + insert + draft.slice(at),
    selection: [caret, caret],
  });
  // Bold, a link, or a color the break is within would otherwise be left open on one line and
  // closed on the next, its markers showing on both.
  const within = !RAW.has(line.kind) && line.spans.some((span) => span.start < at && at < span.end);
  if (line.kind === 'rule') {
    const next = `${draft.slice(0, line.end)}\n${draft.slice(line.end)}`;
    return { text: next, selection: [line.end + 1, line.end + 1] };
  }
  // In a table, a row below, as a cell can't break; an empty last row leaves the table instead,
  // as an empty list item leaves its list.
  if (TABLE.has(line.kind)) {
    const last = rowsOf(tableAt(all, at) ?? []).at(-1);
    if (line === last && !line.header && line.cells.every((cell) => cell.from === cell.to))
      return {
        text: draft.slice(0, line.start) + draft.slice(line.end),
        selection: [line.start, line.start],
      };
    return editTable(draft, [at, at], 'addRow');
  }
  const block =
    line.kind === 'code' && !soft
      ? codeBlocks(all).find((lines) => lines.includes(line))
      : undefined;
  const open = block?.[0];
  const close = block?.at(-1);
  const above = block?.at(-3);
  const empty = (one: Line | undefined) => one?.kind === 'code' && one.start === one.end;
  if (block && open && close && line === block.at(-2) && empty(line)) {
    // Its fences aren't shown, so with nothing in it, the block goes, as an empty item does.
    if (block.slice(1, -1).every(empty))
      return {
        text: draft.slice(0, open.start) + draft.slice(close.end),
        selection: [open.start, open.start],
      };
    // The third Enter at the end of its code leaves it, as in Jira: the two empty lines go, and
    // the caret goes to a new line after it.
    if (above && empty(above)) {
      const removedAt = above.start - 1;
      const rest = draft.slice(0, removedAt) + draft.slice(line.end);
      const end = close.end - (line.end - removedAt);
      return {
        text: `${rest.slice(0, end)}\n${rest.slice(end)}`,
        selection: [end + 1, end + 1],
      };
    }
  }
  if (soft || line.kind === 'paragraph' || line.kind === 'heading' || line.kind === 'code')
    return within ? splitLine(draft, line, at, '') : splice('\n');
  if (line.kind === 'fence') {
    const closed = all.slice(all.indexOf(line) + 1).some((later) => later.kind === 'fence');
    if (!line.opens || closed || at !== line.end) return splice('\n');
    const fence = /`{3,}|~{3,}/u.exec(draft.slice(line.start, line.end))?.[0] ?? '```';
    return splice(`\n\n${fence}`, at + 1);
  }
  if (!draft.slice(line.from, line.end).trim()) {
    if (LIST.has(line.kind) && line.depth > 0) {
      const outdented = indent(draft, [at, at], true);
      if (outdented) return outdented;
    }
    return {
      text: draft.slice(0, line.start) + draft.slice(line.end),
      selection: [line.start, line.start],
    };
  }
  const marker = continuation(draft, line);
  const next = within ? splitLine(draft, line, at, marker) : splice(`\n${marker}`);
  return { text: renumber(next.text, all.indexOf(line) + 1), selection: next.selection };
}

function markerFor(block: Block, line: Line, text: string, number: number): string {
  const indent = LIST.has(line.kind) ? indentOf(text, line) : '';
  switch (block) {
    case 'paragraph':
    case 'code':
      return '';
    case 'heading1':
    case 'heading2':
    case 'heading3':
    case 'heading4':
    case 'heading5':
    case 'heading6':
      return `${'#'.repeat(Number(block.slice(-1)))} `;
    case 'quote':
      return '> ';
    case 'bullet':
      return `${indent}- `;
    case 'task':
      return line.kind === 'task' ? text.slice(line.start, line.from) : `${indent}- [ ] `;
    case 'ordered':
      return `${indent}${String(number)}. `;
  }
}

/** An offset once one line's marker, before it, became `length` characters long. */
function shifted(offset: number, line: Line, length: number): number {
  if (offset < line.start) return offset;
  if (offset <= line.from) return line.start + length;
  return offset + length - (line.from - line.start);
}

/**
 * Makes the selected lines a heading, a quote, a list, or text, or puts them in a code block. A
 * list, heading, or quote they already all are turns back into text.
 */
export function setBlock(text: string, selection: readonly [number, number], block: Block): Edit {
  const [start, end] = ordered(selection[0], selection[1]);
  const all = parseMarkdown(text);
  const chosen = selectedLines(all, start, end);
  if (block === 'code') return codeBlock(text, all, chosen, [start, end]);
  // A divider stays one: it has no words to make anything else of. A table's rows stay rows.
  const touched = chosen.filter(
    (line) => !RAW.has(line.kind) && line.kind !== 'rule' && !TABLE.has(line.kind),
  );
  // Several lines become a list, but the empty lines between them stay empty.
  const several = touched.length > 1 && block !== 'paragraph' && !block.startsWith('heading');
  const targets = several
    ? touched.filter((line) => text.slice(line.from, line.end).trim())
    : touched;
  const top = targets[0];
  if (!top) return { text, selection: [start, end] };
  const target: Block =
    block !== 'paragraph' && targets.every((line) => blockOf(line) === block) ? 'paragraph' : block;
  // A numbered list carries on from one just above it.
  const above = all[all.indexOf(top) - 1];
  let number =
    target === 'ordered' && above?.kind === 'ordered' && above.depth === 0
      ? Number.parseInt(above.number, 10) + 1
      : 1;
  const markers = targets.map((line) => markerFor(target, line, text, number++));
  let next = text;
  let [selStart, selEnd] = [start, end];
  for (let index = targets.length - 1; index >= 0; index -= 1) {
    const line = targets[index];
    const marker = markers[index] ?? '';
    if (!line) continue;
    next = next.slice(0, line.start) + marker + next.slice(line.from);
    selStart = shifted(selStart, line, marker.length);
    selEnd = shifted(selEnd, line, marker.length);
  }
  return { text: next, selection: [selStart, selEnd] };
}

/** Puts the selected lines in a code block, or takes away the code block they're in. */
function codeBlock(
  text: string,
  all: readonly Line[],
  chosen: readonly Line[],
  [start, end]: [number, number],
): Edit {
  const first = chosen[0];
  const last = chosen.at(-1);
  if (!first || !last) return { text, selection: [start, end] };
  if (!chosen.every((line) => RAW.has(line.kind))) {
    const fence = '```\n';
    const next = `${text.slice(0, first.start)}${fence}${text.slice(first.start, last.end)}\n\`\`\`${text.slice(last.end)}`;
    return { text: next, selection: [start + fence.length, end + fence.length] };
  }
  let open = all.indexOf(first);
  while (open > 0 && !(all[open]?.kind === 'fence' && all[open]?.opens)) open -= 1;
  let close = all.indexOf(last);
  while (close < all.length - 1 && !(all[close]?.kind === 'fence' && !all[close]?.opens))
    close += 1;
  const opening = all[open];
  const closing = all[close];
  if (!opening?.opens) return { text, selection: [start, end] };
  let next = text;
  let [selStart, selEnd] = [start, end];
  // The closing fence first, with the line break before it, then the opening one with its own.
  if (closing?.kind === 'fence' && !closing.opens && closing !== opening) {
    const from = closing.start - 1;
    next = next.slice(0, from) + next.slice(closing.end);
    const lift = (offset: number) =>
      offset <= from ? offset : Math.max(from, offset - (closing.end - from));
    [selStart, selEnd] = [lift(selStart), lift(selEnd)];
  }
  const removed = Math.min(opening.end + 1, next.length) - opening.start;
  next = next.slice(0, opening.start) + next.slice(opening.start + removed);
  const lift = (offset: number) => (offset <= opening.end ? opening.start : offset - removed);
  return { text: next, selection: [lift(selStart), lift(selEnd)] };
}

/**
 * Puts a divider in, as Jira's toolbar does: an empty line becomes one, and otherwise it goes
 * after the line the caret is in. The caret goes on to the line after it, made if need be.
 */
export function insertDivider(text: string, selection: readonly [number, number]): Edit {
  const [start, end] = ordered(selection[0], selection[1]);
  // Code is taken as it's written: a divider there would only be dashes.
  if (RAW.has(lineAt(parseMarkdown(text), start).kind)) return { text, selection: [start, end] };
  const removed = removeText(text, [start, end]);
  const draft = removed.text;
  const line = lineAfter(draft, removed.selection[0]);
  const empty = line.kind === 'paragraph' && !draft.slice(line.start, line.end).trim();
  const head = empty ? `${draft.slice(0, line.start)}---` : `${draft.slice(0, line.end)}\n---`;
  const rest = draft.slice(line.end);
  // The line after it, where the caret goes: its text, after any marker it has.
  if (rest) {
    const next = head + rest;
    const at = caretPlace(next, head.length + 1);
    return { text: next, selection: [at, at] };
  }
  return { text: `${head}\n`, selection: [head.length + 1, head.length + 1] };
}

/** The line something put in at `offset` goes after: its own, or in a table, the table's last. */
function lineAfter(text: string, offset: number): Line {
  const all = parseMarkdown(text);
  const line = lineAt(all, offset);
  return (TABLE.has(line.kind) ? tableAt(all, offset)?.at(-1) : null) ?? line;
}

/**
 * Puts a table in, as Jira's toolbar does: three columns, a header, and two rows, in place of an
 * empty line, or after the line the caret is in, with a line after it to carry on writing in. The
 * caret goes in its first cell.
 */
export function insertTable(text: string, selection: readonly [number, number]): Edit {
  const [start, end] = ordered(selection[0], selection[1]);
  if (RAW.has(lineAt(parseMarkdown(text), start).kind)) return { text, selection: [start, end] };
  const removed = removeText(text, [start, end]);
  const draft = removed.text;
  const line = lineAfter(draft, removed.selection[0]);
  const empty = line.kind === 'paragraph' && !draft.slice(line.start, line.end).trim();
  const head = empty ? draft.slice(0, line.start) : `${draft.slice(0, line.end)}\n`;
  const table = writeGrid([0, 1, 2].map(() => ['', '', '']));
  const next = `${head}${table}${draft.slice(line.end) || '\n'}`;
  const at = placeInTable(next, head.length, 0, 0);
  return { text: next, selection: [at, at] };
}

/**
 * Tab and Shift+Tab in a table: the next cell's text selected, or the one before's, row after row.
 * Tab in the last cell adds a row. Null where the selection isn't in a table.
 */
export function moveCell(
  text: string,
  selection: readonly [number, number],
  back = false,
): Edit | null {
  const [start, end] = ordered(selection[0], selection[1]);
  const all = parseMarkdown(text);
  const line = lineAt(all, start);
  const table = tableAt(all, start);
  if (line.kind !== 'row' || !table) return null;
  const rows = rowsOf(table);
  const row = rows.indexOf(line);
  const cells = rows.flatMap((one, index) => one.cells.map((cell) => ({ cell, row: index })));
  const here = cells.findIndex((one) => one.cell === line.cells[cellIndex(line, start)]);
  const target = cells[here + (back ? -1 : 1)];
  if (target) return { text, selection: [target.cell.from, target.cell.to] };
  if (back || row !== rows.length - 1) return { text, selection: [start, end] };
  const added = editTable(text, [start, start], 'addRow');
  const at = placeInTable(added.text, table[0]?.start ?? 0, rows.length, 0);
  return { text: added.text, selection: [at, at] };
}

/**
 * Changes the table the selection is in: a row after the caret's, or a column after its cell, or
 * takes away its row, its column, or the whole table. Taking a table's header away makes its
 * first row the header; taking its last row or column takes the table.
 */
export function editTable(
  text: string,
  selection: readonly [number, number],
  action: TableAction,
): Edit {
  const [start, end] = ordered(selection[0], selection[1]);
  const all = parseMarkdown(text);
  const table = tableAt(all, start);
  const top = table?.[0]?.start;
  const bottom = table?.at(-1)?.end;
  if (!table || top === undefined || bottom === undefined) return { text, selection: [start, end] };
  const rows = rowsOf(table);
  const here = lineAt(all, start);
  const row = Math.max(0, rows.indexOf(here));
  const column = here.kind === 'row' ? cellIndex(here, start) : 0;
  const grid = rows.map((line) => line.cells.map((cell) => text.slice(cell.from, cell.to)));
  const columns = Math.max(...grid.map((cells) => cells.length));
  let place: [number, number] | null = null;
  switch (action) {
    case 'addRow':
      grid.splice(row + 1, 0, Array<string>(columns).fill(''));
      place = [row + 1, column];
      break;
    case 'addColumn':
      for (const cells of grid) {
        while (cells.length < columns) cells.push('');
        cells.splice(column + 1, 0, '');
      }
      place = [row, column + 1];
      break;
    case 'deleteRow':
      if (grid.length > 1) {
        grid.splice(row, 1);
        place = [Math.min(row, grid.length - 1), column];
      }
      break;
    case 'deleteColumn':
      if (columns > 1) {
        for (const cells of grid) cells.splice(column, 1);
        place = [row, Math.min(column, columns - 2)];
      }
      break;
    case 'deleteTable':
      break;
  }
  if (!place) {
    // The table goes with a line break, the one after it, or at the end, the one before.
    const [from, to] = bottom < text.length ? [top, bottom + 1] : [Math.max(0, top - 1), bottom];
    const next = text.slice(0, from) + text.slice(to);
    const at = caretPlace(next, Math.min(from, next.length));
    return { text: next, selection: [at, at] };
  }
  const next = text.slice(0, top) + writeGrid(grid) + text.slice(bottom);
  const at = placeInTable(next, top, place[0], place[1]);
  return { text: next, selection: [at, at] };
}

/**
 * Tab and Shift+Tab in a list: the selected items a level in, under the item above, or a level
 * out. Null where none of the selected lines are list items, so Tab moves on as it would.
 */
export function indent(
  text: string,
  selection: readonly [number, number],
  outdent = false,
): Edit | null {
  const [start, end] = ordered(selection[0], selection[1]);
  const all = parseMarkdown(text);
  const items = selectedLines(all, start, end).filter((line) => LIST.has(line.kind));
  const first = items[0];
  if (!first) return null;
  const above = all.slice(0, all.indexOf(first)).reverse();
  const nearest = (depth: number) =>
    above.find((line) => LIST.has(line.kind) && line.depth === depth);
  let target: number;
  if (outdent) {
    if (first.depth === 0) return { text, selection: [start, end] };
    const parent = nearest(first.depth - 1);
    target = parent ? indentOf(text, parent).length : 0;
  } else {
    // Under the item above, in line with its text.
    const sibling = nearest(first.depth);
    if (!sibling) return { text, selection: [start, end] };
    target = sibling.from - sibling.start;
  }
  const delta = target - indentOf(text, first).length;
  let next = text;
  let [selStart, selEnd] = [start, end];
  for (const line of [...items].reverse()) {
    const now = indentOf(text, line).length;
    const wanted = Math.max(0, now + delta);
    next = next.slice(0, line.start) + ' '.repeat(wanted) + next.slice(line.start + now);
    const move = (offset: number) =>
      offset <= line.start ? offset : Math.max(line.start + wanted, offset + wanted - now);
    [selStart, selEnd] = [move(selStart), move(selEnd)];
  }
  return { text: next, selection: [selStart, selEnd] };
}

/** Ticks a checklist item, or unticks it. Null for a line that isn't one. */
export function toggleTask(text: string, offset: number): Edit | null {
  const line = lineAt(parseMarkdown(text), offset);
  if (line.kind !== 'task') return null;
  const box = text.indexOf('[', line.start);
  if (box === -1 || box > line.from) return null;
  const next = text.slice(0, box + 1) + (line.checked ? ' ' : 'x') + text.slice(box + 2);
  return { text: next, selection: [offset, offset] };
}

/** A link's address as its words, as people say it: without https:// or mailto:. */
function linkWords(href: string): string {
  return href
    .replace(/^https?:\/\/(?:www\.)?/iu, '')
    .replace(/^mailto:/iu, '')
    .replace(/\/$/u, '');
}

/**
 * Links the selected words to `href`, or with `href` null, unlinks them. A caret in a link
 * changes where it goes, or unlinks it; a caret within a word links the word; anywhere else, the
 * address goes in, as its own words.
 */
export function setLink(
  text: string,
  selection: readonly [number, number],
  href: string | null,
): Edit {
  let [start, end] = ordered(selection[0], selection[1]);
  const line = lineAt(parseMarkdown(text), start);
  if (RAW.has(line.kind)) return { text, selection: [start, end] };
  if (start === end) {
    const link = line.spans.find(
      (span) => span.kind === 'link' && span.from <= start && start <= span.to,
    );
    const word = wordAt(text, start);
    if (link) [start, end] = [link.from, link.to];
    else if (word) [start, end] = word;
    else if (href) {
      const words = linkWords(href) || href;
      const next = `${text.slice(0, start)}[${words}](${href})${text.slice(start)}`;
      return { text: next, selection: [start + 1, start + 1 + words.length] };
    } else return { text, selection: [start, end] };
  }
  return reformat(text, [start, Math.min(end, line.end)], (glyph) => ({
    ...glyph,
    marks: { ...glyph.marks, href: href ?? undefined },
  }));
}

/**
 * What a person typed for a link, made into one: https:// before a bare address, mailto: before
 * an email address. Null for what a link shouldn't open, such as a script, or what isn't one.
 */
export function linkTarget(typed: string): string | null {
  // Spaces and brackets would end the link early in Markdown.
  const value = typed.trim().replace(/\s/gu, '%20').replace(/\(/gu, '%28').replace(/\)/gu, '%29');
  if (!value) return null;
  let href: string;
  if (/^[a-z][a-z\d+.-]*:/iu.test(value)) {
    if (!/^(?:https?|mailto|tel):/iu.test(value)) return null;
    href = value;
  } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(value)) href = `mailto:${value}`;
  else if (/^[^\s/]+\.[^\s/]+(?:\/\S*)?$/u.test(value)) href = `https://${value}`;
  else return null;
  return href;
}

/** Takes all formatting from the selected words, and makes their lines plain text again. */
export function clearFormatting(text: string, selection: readonly [number, number]): Edit {
  const [start, end] = ordered(selection[0], selection[1]);
  const cleared =
    start === end
      ? { text, selection: [start, end] as const }
      : reformat(text, [start, end], (glyph) => ({ ...glyph, marks: {} }));
  const [from, to] = cleared.selection;
  const formatted = selectedLines(parseMarkdown(cleared.text), from, to).some(
    (line) => line.kind !== 'paragraph' && !RAW.has(line.kind),
  );
  return formatted ? setBlock(cleared.text, cleared.selection, 'paragraph') : cleared;
}

/** The formatting where the selection is, with formatting pending for the next words typed. */
export function formatsAt(
  text: string,
  selection: readonly [number, number],
  pending: Pending = {},
): Formats {
  const [start, end] = ordered(selection[0], selection[1]);
  const all = parseMarkdown(text);
  const chosen = selectedLines(all, start, end);
  const blocks = new Set(chosen.map(blockOf));
  const block = blocks.size === 1 ? ([...blocks][0] ?? null) : null;
  const marks = new Set<Format>();
  let link: string | null = null;
  let color: TextColor | null = null;
  let highlight: Highlight | null = null;
  if (start === end) {
    for (const span of lineAt(all, start).spans) {
      if (span.from > start || start > span.to) continue;
      if (span.kind === 'link') link = span.href ?? null;
      else if (span.kind === 'color') color = span.color as TextColor;
      else if (span.kind === 'highlight') highlight = span.color as Highlight;
      else marks.add(span.kind);
    }
  } else {
    const glyphs = chosen
      .filter((line) => !RAW.has(line.kind))
      .flatMap((line) => glyphsOf(text, line))
      .filter((glyph) => glyph.at >= start && glyph.at < end && !SPACE.test(glyph.char));
    for (const kind of FORMATS) {
      if (glyphs.length > 0 && glyphs.every((glyph) => glyph.marks[kind])) marks.add(kind);
    }
    const same = <Value>(value: (marks: Marks) => Value | undefined): Value | null => {
      const first = glyphs[0] && value(glyphs[0].marks);
      return first !== undefined && glyphs.every((glyph) => value(glyph.marks) === first)
        ? first
        : null;
    };
    link = same((one) => one.href);
    color = same((one) => one.color);
    highlight = same((one) => one.highlight);
  }
  for (const kind of FORMATS) {
    if (pending[kind] === true) marks.add(kind);
    else if (pending[kind] === false) marks.delete(kind);
  }
  if (pending.color !== undefined) color = pending.color;
  if (pending.highlight !== undefined) highlight = pending.highlight;
  const table = TABLE.has(lineAt(all, start).kind);
  return { marks, link, block, color, highlight, table };
}

/**
 * Colors the selected words, or highlights them, or with null, takes their color or highlight
 * away. A caret within a word colors the word; anywhere else there's nothing to color, and it's
 * null: the next words typed can take the color instead (see `insertText`).
 */
export function setPaint(
  text: string,
  selection: readonly [number, number],
  kind: Paint,
  value: TextColor | Highlight | null,
): Edit | null {
  let [start, end] = ordered(selection[0], selection[1]);
  const caret = start === end ? start : null;
  if (caret !== null) {
    const word = wordAt(text, caret);
    if (!word) return null;
    [start, end] = word;
  }
  const words = selectedLines(parseMarkdown(text), start, end)
    .filter((line) => !RAW.has(line.kind))
    .flatMap((line) => glyphsOf(text, line))
    .some((glyph) => glyph.at >= start && glyph.at < end && !SPACE.test(glyph.char));
  if (!words) return null;
  return reformat(
    text,
    [start, end],
    (glyph) => ({ ...glyph, marks: { ...glyph.marks, [kind]: value ?? undefined } }),
    caret,
  );
}

/** One character on from the caret, as the text reads: past any formatting written there. */
export function stepForward(text: string, caret: number): number {
  const line = lineAt(parseMarkdown(text), caret);
  const index = cellIndex(line, caret);
  const cell = line.cells[index];
  if (line.kind === 'row' && cell) {
    let at = inCell(line, caret);
    while (at < cell.to && isHidden(line, at)) at += 1;
    if (at < cell.to) return graphemeAfter(text, at, cell.to);
    const next = line.cells[index + 1];
    if (next) return next.from;
  }
  let at = Math.max(caret, line.from);
  while (at < line.end && isHidden(line, at)) at += 1;
  if (at >= line.end) return line.end < text.length ? caretPlace(text, line.end + 1) : text.length;
  return graphemeAfter(text, at, line.end);
}

/**
 * An offset where the caret can be: not within the marker that makes a line a list item, a
 * heading, or a quote, which reads as nothing, but where the line's own text starts after it.
 */
export function caretPlace(text: string, offset: number): number {
  const all = parseMarkdown(text);
  const line = lineAt(all, offset);
  // In a table, in a cell's text, never among the pipes; the line under its header has nothing to
  // be in, so the caret goes on to the line after it, or back to the header.
  if (line.kind === 'row') return inCell(line, offset);
  if (line.kind === 'delimiter') {
    const next = all[all.indexOf(line) + 1];
    if (next) return caretPlace(text, next.start);
    return all[all.indexOf(line) - 1]?.cells.at(-1)?.to ?? offset;
  }
  // A closed code block's fences aren't shown: the caret goes to the start of its code, or the end.
  if (line.kind === 'fence') {
    const block = codeBlocks(all).find((lines) => lines.includes(line));
    const code = line.opens ? block?.[1] : block?.at(-2);
    if (code?.kind === 'code') return line.opens ? code.start : code.end;
  }
  return line.from > line.start && offset >= line.start && offset < line.from ? line.from : offset;
}

/**
 * The part of the draft to copy for a selection: with the formatting around the words it covers
 * whole, and the markers of the lines whose text it covers whole, so the copy reads the same.
 */
export function copyRange(text: string, selection: readonly [number, number]): [number, number] {
  let [start, end] = ordered(selection[0], selection[1]);
  if (start === end) return [start, end];
  const all = parseMarkdown(text);
  // A table's words within one cell are copied as they are; any more of it, the whole table.
  const row = lineAt(all, start);
  const cell = row.cells[cellIndex(row, start)];
  const inOneCell = row.kind === 'row' && cell && start >= cell.from && end <= cell.to;
  for (const table of inOneCell ? [] : tablesIn(all)) {
    const top = table[0]?.start ?? 0;
    const bottom = table.at(-1)?.end ?? 0;
    if (top > end || bottom < start) continue;
    start = Math.min(start, top);
    end = Math.max(end, bottom);
  }
  for (const line of selectedLines(all, start, end)) {
    for (const span of line.spans) {
      if (span.from >= start && span.to <= end) {
        start = Math.min(start, span.start);
        end = Math.max(end, span.end);
      }
    }
    if (line.from > line.start && start >= line.start && start <= line.from && end >= line.end)
      start = line.start;
  }
  return [start, end];
}
